import React from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo2.png";
import { FaArrowLeft } from "react-icons/fa";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-bold text-gray-900 text-base">{title}</h2>
      {children}
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-gray-600 leading-relaxed">
      {items.map((it) => (
        <li key={it}>{it}</li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border border-gray-100 shrink-0">
              <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">UCH Self Service</p>
              <p className="text-gray-400 text-[11px]">University College Hospital</p>
            </div>
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3d2e] border border-green-200 rounded-xl px-4 py-2 hover:bg-green-50 transition mb-6"
        >
          <FaArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 space-y-7">
          <div>
            <p className="text-xs font-bold text-green-700 uppercase tracking-widest mb-2">
              University College Hospital, Ibadan
            </p>
            <h1 className="text-3xl font-black text-gray-900 mb-1.5">
              Privacy Policy for Hospital Wallet Platform
            </h1>
          </div>

          <Section title="Introduction">
            <p className="text-sm text-gray-600 leading-relaxed">
              This Privacy Policy explains how University College Hospital, Ibadan (&ldquo;the
              Hospital&rdquo;) collects, uses, stores, protects, and discloses personal
              information obtained through the UCH Hospital Wallet Platform (&ldquo;Wallet
              Platform&rdquo; or &ldquo;Platform&rdquo;).
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              The UCH Hospital Wallet Platform is a digital payment and healthcare wallet
              solution that enables patients, sponsors, and authorized users to fund
              healthcare services, make payments for medical services, manage transactions,
              and request refunds.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              We are committed to protecting your privacy and processing your personal
              information in compliance with the Nigeria Data Protection Act (NDPA) 2023 and
              other applicable laws and regulations.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              By registering for, accessing, or using the Hospital Wallet Platform, you
              acknowledge that you have read and understood this Privacy Policy and consent
              to the processing of your personal information as described herein.
            </p>
          </Section>

          <Section title="Definitions">
            <p className="text-sm text-gray-600 leading-relaxed">For the purpose of this Privacy Policy:</p>
            <List
              items={[
                "“Personal Data” means any information that identifies or can identify an individual.",
                "“Sensitive Personal Data” includes health information, medical records, biometric data, financial information, and any other information classified as sensitive under applicable law.",
                "“Processing” means any operation performed on personal data including collection, recording, storage, use, disclosure, transfer, or deletion.",
                "“Data Subject” means the individual whose personal data is being processed.",
                "“Sponsor” means a person, organization, employer, insurer, or entity funding a patient's healthcare expenses through the Wallet Platform.",
                "“Wallet Account” means the digital account assigned to a patient or authorized user for healthcare-related transactions.",
              ]}
            />
          </Section>

          <Section title="Information We Collect">
            <p className="text-sm text-gray-600 leading-relaxed">
              We may collect and process the following categories of information:
            </p>
            <p className="text-sm font-semibold text-gray-800 mt-3">Personal Identification Information</p>
            <List
              items={[
                "Full name",
                "Date of birth",
                "Gender",
                "Nationality",
                "Residential address",
                "Phone number",
                "Email address",
                "National Identification Number (where applicable)",
                "Passport photograph",
                "Means of identification",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Patient Information</p>
            <List
              items={[
                "Hospital number",
                "Patient registration details",
                "Next of Kin information",
                "Emergency contact information",
                "Medical record references",
                "Healthcare service history",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Financial Information</p>
            <List
              items={[
                "Bank account details",
                "Payment card information (processed through approved payment providers)",
                "Wallet transaction history",
                "Funding records",
                "Refund requests and refund history",
                "Payment verification records",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Technical Information</p>
            <List
              items={[
                "Device information",
                "Browser type",
                "IP address",
                "Login history",
                "System logs",
                "Usage analytics",
                "Access timestamps",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Communications Information</p>
            <List
              items={[
                "Customer support requests",
                "Complaints",
                "Feedback",
                "Correspondence with the Hospital",
              ]}
            />
          </Section>

          <Section title="How We Collect Information">
            <p className="text-sm font-semibold text-gray-800">Direct Collection</p>
            <p className="text-sm text-gray-600 leading-relaxed">Information provided when:</p>
            <List
              items={[
                "Registering for the Wallet",
                "Funding a Wallet account",
                "Requesting refunds",
                "Updating account information",
                "Contacting customer support",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Hospital Records</p>
            <List
              items={[
                "Patient registration records",
                "Medical records",
                "Admission records",
                "Billing records",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Automated Collection</p>
            <List
              items={[
                "Website interactions",
                "Mobile application usage",
                "System logs",
                "Security monitoring tools",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Third-Party Sources</p>
            <List
              items={[
                "Payment processors",
                "Banks",
                "Government agencies",
                "Identity verification providers",
                "Regulatory authorities",
              ]}
            />
          </Section>

          <Section title="Purpose of Processing">
            <p className="text-sm text-gray-600 leading-relaxed">
              We process personal information for the following purposes:
            </p>
            <p className="text-sm font-semibold text-gray-800 mt-3">Service Delivery</p>
            <List
              items={[
                "Creating and managing Wallet accounts",
                "Processing payments",
                "Managing deposits",
                "Facilitating refunds",
                "Verifying transactions",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Healthcare Administration</p>
            <List
              items={[
                "Linking Wallets to patient records",
                "Supporting healthcare billing processes",
                "Managing patient financial accounts",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Security and Fraud Prevention</p>
            <List
              items={[
                "Identity verification",
                "Detection of fraudulent activities",
                "Prevention of unauthorized access",
                "Risk management",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Regulatory Compliance</p>
            <List
              items={[
                "Compliance with NDPA 2023",
                "Financial reporting obligations",
                "Audit requirements",
                "Anti-money laundering obligations",
                "Legal and regulatory requests",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Communication</p>
            <List
              items={[
                "Service notifications",
                "Transaction alerts",
                "Security alerts",
                "Customer support communications",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Platform Improvement</p>
            <List
              items={[
                "Analytics",
                "Performance monitoring",
                "Service enhancement",
                "User experience optimization",
              ]}
            />
          </Section>

          <Section title="Legal Basis of Processing">
            <p className="text-sm text-gray-600 leading-relaxed">
              We process personal information based on one or more of the following lawful
              grounds:
            </p>
            <List
              items={[
                "Consent of the data subject",
                "Performance of a contract",
                "Compliance with legal obligations",
                "Protection of vital interests of patients",
                "Legitimate interests of the Hospital",
                "Provision of healthcare services",
              ]}
            />
          </Section>

          <Section title="Disclosure of Information">
            <p className="text-sm text-gray-600 leading-relaxed">We may share information with:</p>
            <p className="text-sm font-semibold text-gray-800 mt-3">Internal Personnel</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Authorized Hospital staff who require access for operational, financial,
              medical, or administrative purposes.
            </p>
            <p className="text-sm font-semibold text-gray-800 mt-3">Service Providers</p>
            <List
              items={[
                "Payment gateway providers",
                "Banks and financial institutions",
                "Cloud hosting providers",
                "IT support providers",
                "Security service providers",
              ]}
            />
            <p className="text-sm font-semibold text-gray-800 mt-3">Regulators and Authorities</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Where required by law, we may disclose information to:
            </p>
            <List items={["Courts", "Law enforcement agencies", "Regulatory authorities", "Government agencies"]} />
            <p className="text-sm font-semibold text-gray-800 mt-3">Healthcare Partners</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Information may be shared with authorized healthcare partners where necessary
              for treatment, payment processing, or healthcare administration.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed font-semibold">
              We do not sell personal information to third parties.
            </p>
          </Section>

          <Section title="Health and Medical Information">
            <p className="text-sm text-gray-600 leading-relaxed">
              Health information is considered highly sensitive and receives enhanced
              protection. Medical information linked to the Wallet Platform shall only be
              accessed, used, or disclosed:
            </p>
            <List
              items={[
                "For healthcare service delivery;",
                "For payment verification;",
                "For audit and compliance purposes;",
                "Where required by law; or",
                "With the patient's consent.",
              ]}
            />
            <p className="text-sm text-gray-600 leading-relaxed">
              All personnel handling medical information are subject to confidentiality
              obligations.
            </p>
          </Section>

          <Section title="Data Retention">
            <p className="text-sm text-gray-600 leading-relaxed">
              We retain personal information only for as long as necessary to:
            </p>
            <List
              items={[
                "Provide services;",
                "Fulfill legal obligations;",
                "Resolve disputes;",
                "Conduct audits;",
                "Maintain healthcare records.",
              ]}
            />
            <p className="text-sm text-gray-600 leading-relaxed">
              Retention periods may vary depending on applicable healthcare, financial, and
              regulatory requirements. Upon expiration of applicable retention periods, data
              may be securely deleted, anonymized, or archived in accordance with legal
              requirements.
            </p>
          </Section>

          <Section title="Data Security">
            <p className="text-sm text-gray-600 leading-relaxed">
              We implement appropriate technical and organizational safeguards, including:
            </p>
            <List
              items={[
                "Encryption of sensitive data;",
                "Secure access controls;",
                "Multi-factor authentication where applicable;",
                "Firewalls and intrusion detection systems;",
                "Audit logging and monitoring;",
                "Staff confidentiality obligations;",
                "Regular security assessments.",
              ]}
            />
            <p className="text-sm text-gray-600 leading-relaxed">
              Despite our efforts, no electronic system can guarantee absolute security.
              Users are responsible for protecting their login credentials and reporting any
              suspected unauthorized activity immediately.
            </p>
          </Section>

          <Section title="International Data Transfers">
            <p className="text-sm text-gray-600 leading-relaxed">
              Where personal information is transferred outside Nigeria, such transfers shall
              only occur where:
            </p>
            <List
              items={[
                "Adequate safeguards exist;",
                "Applicable legal requirements are satisfied; and",
                "Appropriate contractual protections are implemented.",
              ]}
            />
          </Section>

          <Section title="Your Rights">
            <p className="text-sm text-gray-600 leading-relaxed">
              Subject to applicable law, you may have the right to:
            </p>
            <List
              items={[
                "Access your personal information;",
                "Request correction of inaccurate information;",
                "Request deletion of information where legally permissible;",
                "Withdraw consent where processing is based on consent;",
                "Restrict certain processing activities;",
                "Object to processing under specific circumstances;",
                "Request portability of your personal information;",
                "Lodge a complaint with the Nigeria Data Protection Commission (NDPC).",
              ]}
            />
            <p className="text-sm text-gray-600 leading-relaxed">
              Requests may be submitted through the Hospital's designated Data Protection
              Officer.
            </p>
          </Section>

          <Section title="Cookies and Digital Tracking">
            <p className="text-sm text-gray-600 leading-relaxed">
              Where the Platform operates through a website or mobile application, cookies
              and similar technologies may be used to:
            </p>
            <List
              items={[
                "Maintain user sessions;",
                "Improve functionality;",
                "Enhance security;",
                "Analyze usage patterns.",
              ]}
            />
            <p className="text-sm text-gray-600 leading-relaxed">
              Users may manage cookie preferences through browser settings where applicable.
            </p>
          </Section>

          <Section title="Children's Privacy">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Wallet Platform may process information relating to minors receiving
              healthcare services. Such processing shall be conducted through parents,
              guardians, sponsors, or other legally authorized representatives and in
              accordance with applicable healthcare and data protection laws.
            </p>
          </Section>

          <Section title="Data Breach Management">
            <p className="text-sm text-gray-600 leading-relaxed">
              In the event of a data breach that may pose a risk to affected individuals, the
              Hospital shall:
            </p>
            <List
              items={[
                "Investigate the incident promptly;",
                "Take reasonable steps to mitigate risks;",
                "Notify affected individuals where required by law; and",
                "Report the incident to relevant regulatory authorities where applicable.",
              ]}
            />
          </Section>

          <Section title="Third-Party Services">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Platform may integrate with third-party payment processors, banking
              systems, or technology providers. The Hospital is not responsible for the
              privacy practices of independent third parties. Users are encouraged to review
              the privacy policies of such third parties.
            </p>
          </Section>

          <Section title="Changes to This Privacy Policy">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Hospital reserves the right to amend this Privacy Policy from time to time.
              Updated versions shall be published on the Hospital's website, patient portal,
              or designated service points. Continued use of the Wallet Platform after
              publication of revisions constitutes acceptance of the updated Privacy Policy.
            </p>
          </Section>

          <Section title="Consent">
            <p className="text-sm text-gray-600 leading-relaxed">
              By registering for, accessing, funding, or using the Hospital Wallet Platform,
              you acknowledge that you have read, understood, and consent to the collection,
              processing, storage, use, and disclosure of your personal information in
              accordance with this Privacy Policy.
            </p>
          </Section>

          <Section title="Contact Us">
            <p className="text-sm text-gray-600 leading-relaxed">
              If you have questions about this Privacy Policy, please reach out via our{" "}
              <a href="/support" className="text-[#0d3d2e] font-semibold hover:underline">
                Support &amp; Contact Us
              </a>{" "}
              page.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
